import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Icon from '../../components/AppIcon';


import LoginForm from './components/LoginForm';
import RegisterForm from './components/RegisterForm';

const AuthenticationScreen = () => {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [isLogin, setIsLogin] = useState(true);
  const [currentLanguage, setCurrentLanguage] = useState('en');

  useEffect(() => {
    const savedLanguage = localStorage.getItem('language') || 'en';
    setCurrentLanguage(savedLanguage);
  }, []);

  useEffect(() => {
    if (!loading && user) {
      navigate('/unified-dashboard');
    }
  }, [user, loading, navigate]);

  const translations = {
    en: {
      welcome: 'Welcome to OGADA',
      subtitle: 'Global marketplace for travelers and buyers',
      switchToRegister: "Don\'t have an account? Sign up",
      switchToLogin: 'Already have an account? Sign in',
      features: [
        'Connect buyers with travelers worldwide',
        'Secure escrow payment system',
        'Real-time deal tracking',
        'Multi-language support'
      ]
    },
    ko: {
      welcome: 'OGADA에 오신 것을 환영합니다',
      subtitle: '여행자와 구매자를 위한 글로벌 마켓플레이스',
      switchToRegister: '계정이 없으신가요? 회원가입',
      switchToLogin: '이미 계정이 있으신가요? 로그인',
      features: [
        '전 세계 구매자와 여행자 연결',
        '안전한 에스크로 결제 시스템',
        '실시간 거래 추적',
        '다국어 지원'
      ]
    }
  };

  const t = translations?.[currentLanguage];

  const toggleLanguage = () => {
    const newLanguage = currentLanguage === 'en' ? 'ko' : 'en';
    setCurrentLanguage(newLanguage);
    localStorage.setItem('language', newLanguage);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col lg:flex-row">
      {/* Left Side - Branding */}
      <div className="lg:w-1/2 bg-gradient-to-br from-primary to-secondary p-8 lg:p-12 flex flex-col justify-center items-center text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-32 h-32 bg-white rounded-full"></div>
          <div className="absolute bottom-20 right-20 w-48 h-48 bg-white rounded-full"></div>
        </div>
        
        <div className="relative z-10 max-w-md text-center lg:text-left">
          <div className="flex items-center justify-center lg:justify-start gap-3 mb-6">
            <img
              src="/assets/images/ogada-logo-1770865214464.png"
              alt="OGADA Logo"
              className="h-28 w-auto object-contain"
              style={{ mixBlendMode: 'multiply' }}
            />
          </div>
          
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold mb-4">
            {t?.welcome}
          </h1>
          <p className="text-lg md:text-xl mb-8 opacity-90">
            {t?.subtitle}
          </p>
          
          <div className="space-y-4">
            {t?.features?.map((feature, index) => (
              <div key={index} className="flex items-center gap-3">
                <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
                  <Icon name="Check" size={16} color="white" />
                </div>
                <p className="text-base opacity-90">{feature}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right Side - Auth Forms */}
      <div className="lg:w-1/2 p-8 lg:p-12 flex flex-col justify-center">
        <div className="max-w-md mx-auto w-full">
          <div className="flex justify-end mb-6">
            <button
              onClick={toggleLanguage}
              className="flex items-center gap-2 px-4 h-10 rounded-lg bg-muted hover:bg-muted/80 transition-base"
            >
              <Icon name="Languages" size={18} />
              <span className="text-sm font-medium">{currentLanguage?.toUpperCase()}</span>
            </button>
          </div>

          {isLogin ? (
            <LoginForm currentLanguage={currentLanguage} />
          ) : (
            <RegisterForm currentLanguage={currentLanguage} />
          )}

          <div className="mt-6 text-center">
            <button
              onClick={() => setIsLogin(!isLogin)}
              className="text-sm text-primary hover:underline"
            >
              {isLogin ? t?.switchToRegister : t?.switchToLogin}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthenticationScreen;